import { Box } from "@mui/material";
import { Check as CheckIcon } from "@mui/icons-material";
import { tokens } from "../../theme";

/**
 * Chip de estado del Design Kit (§04): píldora con fondo suave, texto del color
 * semántico y un punto del mismo color. Único lugar donde se define ese estilo.
 *
 * - `denso` (dentro de tablas): sin punto y con menos padding.
 * - `icono="check"`: reemplaza el punto por un ✓ (estado "Aprobado" del kit). Se
 *   muestra también en el denso: el punto es adorno y el ✓ dice algo.
 * - `contorno`: el mismo color pero dibujado, sin relleno. Es para los estados que
 *   están de paso —entregado, en observación— frente a los que ya son un desenlace:
 *   se distinguen de un vistazo sin inventar un color nuevo para cada uno.
 *
 * Para mapear los estados del dominio a un tono, usa `tonoDeTema` /
 * `tonoDePostulacion` en `utils/estados.ts` — no inventes tonos aquí.
 */
export type TonoEstado = keyof typeof tokens.chip;

export default function EstadoChip({
  label,
  tono,
  denso = false,
  icono,
  contorno = false,
}: {
  label: string;
  tono: TonoEstado;
  denso?: boolean;
  icono?: "check";
  contorno?: boolean;
}) {
  const { bg, fg } = tokens.chip[tono];
  // El punto es del chip ancho; el ✓ va siempre que se pida, también en el denso. Son
  // dos cosas distintas: el punto es adorno y el ✓ dice algo (kit §04).
  const conPunto = !denso && !icono;
  // El borde se come un píxel por lado: se descuenta del padding para que un chip
  // dibujado y uno relleno midan lo mismo y no bailen entre ellos en una fila.
  const alto = denso ? 4 : 5;
  const ancho = denso ? 11 : 12;

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        bgcolor: contorno ? "transparent" : bg,
        color: fg,
        border: contorno ? `1px solid ${fg}` : undefined,
        fontWeight: 600,
        fontSize: 12,
        padding: contorno ? `${alto - 1}px ${ancho - 1}px` : `${alto}px ${ancho}px`,
        borderRadius: "500px",
        whiteSpace: "nowrap",
      }}
    >
      {icono === "check" && <CheckIcon sx={{ fontSize: 14 }} />}
      {conPunto && <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: fg, flexShrink: 0 }} />}
      {label}
    </Box>
  );
}
